import { Box, Link } from '@chakra-ui/react'
import NextLink from 'next/link'
import { FC, Fragment, ReactNode } from 'react'

// "[label](href)" links and "[3]" citations (a run like "[3][4]" stays together).
const TOKEN = /\[([^\]]+)\]\(([^)\s]+)\)|((?:\[\d+\])+)/g

/** Plain text for metadata and JSON-LD: links keep their label, citations go away. */
export const toPlainText = (text: string): string =>
  text
    .replace(TOKEN, (_whole, label: string | undefined) => label ?? '')
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim()

/** Cited reference numbers, in order of appearance. */
export const citationsOf = (text: string): number[] =>
  Array.from(text.matchAll(/\[(\d+)\](?!\()/g), (match) => Number(match[1]))

const isInternal = (href: string) => href.startsWith('/') || href.startsWith('#')

// Dark gold on the cream background (readable contrast); the theme's Link drops the underline otherwise.
const linkSx = { color: 'yellow.90', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'yellow.60' }

export const TextLink: FC<{ href: string; children: ReactNode }> = ({ href, children }) =>
  isInternal(href) ? (
    <Link as={NextLink} href={href} prefetch={false} sx={linkSx}>
      {children}
    </Link>
  ) : (
    <Link href={href} sx={linkSx} isExternal>
      {children}
    </Link>
  )

/**
 * Renders the landing's rich text: links, and citations as superscript links to #ref-n that never wrap
 * onto a line of their own (they stay glued to the word before them).
 */
const RichText: FC<{ text: string }> = ({ text }) => {
  const nodes: ReactNode[] = []
  let last = 0
  for (const match of Array.from(text.matchAll(TOKEN))) {
    const [whole, label, href, citations] = match
    const start = match.index!
    if (citations) {
      // Glue the citation to the word right before it.
      const before = text.slice(last, start).match(/^([\s\S]*?)(\S*)$/)!
      nodes.push(before[1])
      nodes.push(
        <Box as="span" key={start} whiteSpace="nowrap">
          {before[2]}
          {Array.from(citations.matchAll(/\[(\d+)\]/g), ([, number]) => (
            <sup key={number}>
              <Link href={`#ref-${number}`} color="yellow.90" fontWeight={700} aria-label={`Referência ${number}`}>
                [{number}]
              </Link>
            </sup>
          ))}
        </Box>
      )
    } else {
      nodes.push(text.slice(last, start))
      nodes.push(
        <TextLink key={start} href={href}>
          {label}
        </TextLink>
      )
    }
    last = start + whole.length
  }
  nodes.push(text.slice(last))
  return (
    <>
      {nodes.map((node, index) => (
        <Fragment key={index}>{node}</Fragment>
      ))}
    </>
  )
}

export default RichText
